import React, { useState, useEffect } from 'react';
import axios from 'axios';
import useLogger from 'hooks/useLogger';

const IMAGE_HOST = 'https://dks.scene7.com/is/image/dkscdn';

/* 
  The custom useScene7MediaSet hook accepts a productStyle from the quickview/ecode api response and initially attempts to use that data 
  to hit the Adobe Scene7 endpoint using their JSONP response. The JSONP response needs heavily stripped to be able to convert it to JSON. 
  Then we extract the media set images and store them in a simple array response with urls to the images in the media set for the UI to render. 
  If selectedColor is set or changes this will re-run to load the new images based on the color selected. The color selected can be updated by using the 
  "setSelectedColor" method returned from this hook.
*/
const useScene7MediaSet = (productStyle, defaultColor = '') => {
  const [productMedia, setProductMedia] = useState({});
  const [selectedColor, setSelectedColor] = useState(defaultColor ? defaultColor : null);
  const [loading, setLoading] = useState(true);
  const { warn } = useLogger();

  const formatProductMedia = (mediaSetResponse) => {
    try {
      if (Array.isArray(mediaSetResponse)) {
        // mediaSetResponse is an array multiple images are returned map over them and create the image link array

        // Filter out media set items that aren't images
        const productImagesOnly = mediaSetResponse.filter((media) => media.i && media.i.n);

        // Map over product images in media set and return them with the url to render them
        return productImagesOnly.map((image) => {
          if (image.i && image.i.n) {
            return `https://dks.scene7.com/is/image/${image.i.n}/?wid=500&qlt=85,0&fmt=jpg&op_sharpen=1`;
          }

          return null;
        });
      }

      // When mediaSetResponse isn't an array there is only one image returned
      const mediaSetArray = [];
      if (mediaSetResponse.i && mediaSetResponse.i.n) {
        const image = `https://dks.scene7.com/is/image/${mediaSetResponse.i.n}/?wid=500&qlt=85,0&fmt=jpg&op_sharpen=1`;
        mediaSetArray.push(image);
      }
      return mediaSetArray;
    } catch (e) {
      warn('useScene7MediaSet - ', e);
      return null;
    }
  };

  const cleanResponse = (responseData) => {
    try {
      const cleanedResponseData = responseData
        .replace('/*jsonp*/', '')
        .replace('customScene7Handler(', '')
        .replace(', "");', '')
        .replace(',"");', '')
        .trim();

      const parsedResponseData = JSON.parse(cleanedResponseData);

      return parsedResponseData;
    } catch (error) {
      warn(error);
      return null;
    }
  };

  const generateScene7Url = () => {
    const productHasColorAttr = productStyle.definingAttributes.findIndex(
      (element) => element.name === 'Color',
    );

    let scene7Url;
    if (productHasColorAttr !== -1 || selectedColor) {
      let color;
      if (selectedColor) {
        // If a color is selected in the attributes use that color instead
        const sanitizedColorName = selectedColor.replace(/[^\w]/gi, '_').replace(/(_+)\1+/g, '$1');
        color = sanitizedColorName;
      } else {
        // If no color is selected use the ecodes style to load a default color
        const productColorAttributes = productStyle.definingAttributes[productHasColorAttr];
        const firstColor = productColorAttributes.value.split('|')[0];
        const sanitizedColorName = firstColor.replace(/[^\w]/gi, '_').replace(/(_+)\1+/g, '$1');
        color = sanitizedColorName;
      }

      scene7Url = `${IMAGE_HOST}/${productStyle.partNumber}_${color}_is?req=set,json,UTF-8&labelkey=label&handler=customScene7Handler`;
    } else {
      scene7Url = `${IMAGE_HOST}/${productStyle.partNumber}_is?req=set,json,UTF-8&labelkey=label&handler=customScene7Handler`;
    }

    return scene7Url;
  };

  useEffect(() => {
    const fetchMediaSet = async () => {
      try {
        setLoading(true);
        const scene7Url = generateScene7Url();

        const response = await axios.get(scene7Url);

        if (response && response.data) {
          response.data = cleanResponse(response.data);

          if (response.data.set && response.data.set.item) {
            const formattedProductImages = formatProductMedia(response.data.set.item);

            setProductMedia(formattedProductImages);
            setLoading(false);
          } else {
            throw Error('Quickview Scene7 - Empty response image set and/or  image set item');
          }
        } else {
          throw Error('Quickview Scene7 - Empty response data.');
        }
      } catch (error) {
        warn(error);
        setLoading(false);
      }
    };
    fetchMediaSet();
  }, [selectedColor]);

  return [productMedia, loading, selectedColor, setSelectedColor];
};

export default useScene7MediaSet;
